<template>
  <div id="reviews-gallery" ref="gallery">
    <pu-flickity
      ref="flickityEl"
      :options="flickityOptions"
      class="reviews-gallery"
      @init="initGallery"
    >
      <div
        v-for="(photo, index) in photos.slice(0, inlineGalleryLimit)"
        ref="gallerySlideElements"
        :key="'photo-' + index"
        class="reviews-carousel-cell carousel-cell"
      >
        <img
          v-if="$isDesktop"
          loading="lazy"
          :src="`/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=264,h=198${photo.url.masterImage}`"
          :srcset="`
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=264,h=198${photo.url.masterImage} 264w,
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=70,w=360,h=270${photo.url.masterImage} 360w,
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=50,w=720,h=540${photo.url.masterImage} 720w
          `"
          :alt="capitalize(photoCaptionAltFormatter(photo.caption))"
          :data-sctrack="`${String($route.name)}--CampsiteReviewsGallery-img-desktop`"
          sizes="264px"
          width="264"
          height="198"
        />
        <img
          v-else
          loading="lazy"
          :src="`/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=264,h=198${photo.url.masterImage}`"
          :srcset="`
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=264,h=198${photo.url.masterImage} 264w,
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=70,w=360,h=270${photo.url.masterImage} 360w,
          `"
          :alt="capitalize(photoCaptionAltFormatter(photo.caption))"
          :data-sctrack="`${String($route.name)}--CampsiteReviewsGallery-img-mobile`"
          sizes="33vw"
          width="143"
          height="107"
        />
      </div>
      <translate
        v-if="photos.length > inlineGalleryLimit"
        ref="viewAllEl"
        tag="div"
        class="reviews-carousel-cell carousel-cell view-all"
      >
        View more
      </translate>
      <div
        v-if="photos.length > inlineGalleryLimit"
        class="reviews-carousel-cell spacer"
      ></div>
    </pu-flickity>

    <campsite-reviews-gallery-fullscreen
      v-if="isFullScreen && photosFull.length"
      :photos="photosFull"
      :initial-index="currentImage"
      @fullscreen-closed="fullscreenClosed"
      @slide-changed="fullscreenSlideChanged"
    />
  </div>
</template>
<script setup lang="ts">
import { useNuxtApp } from 'nuxt/app'
import { capitalize } from '~/utility/text'
import { photoCaptionAltFormatter } from '~/utility/photoCaptionAltFormatter'
import { PhotoResult } from '../types'
const { $isDesktop } = useNuxtApp()

const emit = defineEmits(['fullscreen-opened'])

defineProps<{
  photos: Array<PhotoResult>
  photosFull: Array<PhotoResult>
}>()

const inlineGalleryLimit = 20
const gallery = ref(null)
const gallerySlideElements = ref(null)
const viewAllEl = ref(null)
const flickityEl = ref(null)
const isFullScreen = ref(false)
const currentImage = ref(1)

function fullscreenSlideChanged(slideIdx) {
  currentImage.value = slideIdx
  flickityEl.value?.selectCell(slideIdx)
}

function fullscreenClosed() {
  isFullScreen.value = false
}

function initGallery() {
  flickityEl.value?.on(
    'staticClick',
    (_event, _pointer, _cellElement, cellIndex) => {
      currentImage.value = cellIndex
      isFullScreen.value = true
      emit('fullscreen-opened')
    },
  )
}

const flickityOptions = {
  initialIndex: 0,
  dragThreshold: 20,
  pageDots: false,
  wrapAround: false,
  lazyLoad: 1,
  cellSelector: '.reviews-carousel-cell',
  selectedAttraction: 0.2,
  friction: 0.8,
  prevNextButtons: true,
  draggable: true,
  contain: true,
  groupCells: true,
  cellAlign: 'left',
}
</script>

<style>
@import url('~/assets/css/flickity-custom.css');
</style>

<style scoped>
.reviews-gallery {
  margin: 0 0 1em 0;
  width: 100%;
}

.reviews-gallery :deep(.flickity-slider) {
  margin-left: 1em;
}

.reviews-gallery .reviews-carousel-cell.spacer {
  display: block;
  content: '';
  height: 100%;
  width: 2.8em;
}

.reviews-carousel-cell {
  width: calc(100% / 3);
  margin-right: 0.4em;
  aspect-ratio: 4/3;
  display: inline;
}

.desktop .reviews-gallery .reviews-carousel-cell {
  width: 10em;
}

.desktop .reviews-gallery :deep(.flickity-slider) {
  margin-left: 0;
}

.carousel-cell.view-all {
  padding: 1em;
  display: flex;
  flex-direction: column;
  justify-content: center;
  cursor: pointer;
  font-size: var(--body);
  border: 1px solid var(--colour-table-borders);
  align-items: center;
  border-radius: var(--border-radius);
}

.desktop .reviews-gallery .carousel-cell.view-all {
  height: 100%;
}

.reviews-gallery img {
  height: auto;
  width: 100%;
  max-height: 100%;
  border-radius: var(--border-radius);
}

.reviews-gallery :deep(.flickity-prev-next-button[disabled]) {
  display: none;
}

.reviews-gallery {
  display: none;
}

.reviews-gallery.flickity-enabled {
  display: block;
}

.photo-count {
  font-size: var(--body);
  color: white;
}
</style>
